<template>
  <form>
    <md-card>
      <md-card-header
        v-if="createMode"
        :data-background-color="dataBackgroundColor"
      >
        <h4 class="title">Business Registration</h4>
        <p class="category">
          Register your business. If you wish to be added to another business
          instead, please contact your business manager.
        </p>
      </md-card-header>
      <md-card-header v-else :data-background-color="dataBackgroundColor">
        <h4 class="title">Business Details</h4>
        <p class="category">
          View or Edit your Business Details.
        </p>
      </md-card-header>
      <md-card-content>
        <div class="md-layout">
          <div class="md-layout-item md-small-size-100 md-size-50">
            <form-group
              label="Business Name"
              input-type="text"
              :validator-object="$v.business.business_name"
            ></form-group>
          </div>
          <div class="md-layout-item md-small-size-100 md-size-50">
            <form-group
              label="Business ABN/ACN"
              input-type="text"
              :input-min-length="9"
              :input-max-length="11"
              :validator-object="$v.business.abn"
            ></form-group>
          </div>
          <div class="md-layout-item md-small-size-100 md-size-50">
            <form-group
              label="Trading Name"
              input-type="text"
              :validator-object="$v.business.trading_name"
            ></form-group>
          </div>
          <div class="md-layout-item md-small-size-100 md-size-50">
            <form-group
              label="Business Phone Number"
              input-type="tel"
              helper-text="Include the area code when entering landline numbers."
              :validator-object="$v.business.phone_number"
            ></form-group>
          </div>
          <div class="md-layout-item md-small-size-100 md-size-50">
            <form-group
              label="Business Email Address"
              input-type="email"
              :validator-object="$v.business.email"
            ></form-group>
          </div>
          <div class="md-layout-item md-small-size-100 md-size-50">
            <form-group
              label="Type of Business"
              :validator-object="$v.business.business_type"
            >
              <template v-slot:input>
                <md-select v-model="$v.business.business_type.$model" md-dense>
                  <md-option
                    v-for="(item, id) in businessTypes"
                    :key="id"
                    :value="item.id"
                    >{{ item.description }}</md-option
                  >
                </md-select>
              </template>
              <span v-if="!$v.business.business_type.required"
                >You must select an option</span
              >
            </form-group>
          </div>
          <div class="md-layout-item md-small-size-100 md-size-50">
            <form-group
              label="Contact Name"
              input-type="text"
              :validator-object="$v.business.contact_name"
            ></form-group>
          </div>
        </div>
        <div class="md-layout mt-3">
          <div class="md-layout-item md-size-100">
            <h6>Business Address</h6>
          </div>
          <div class="md-layout-item md-small-size-100 md-size-100">
            <form-group
              label=""
              :validator-object="$v.business.business_address"
            >
              <template v-slot:input>
                <vue-google-autocomplete
                  id="business-address"
                  ref="addressInput"
                  classname="form-control md-input"
                  placeholder="Start typing..."
                  country="au"
                  @placechanged="getAddressData"
                ></vue-google-autocomplete>
              </template>
            </form-group>
          </div>
        </div>
        <div class="md-layout mt-3">
          <div class="md-layout-item md-size-100">
            <h6>Postal Address</h6>
          </div>
          <div class="md-layout-item md-size-100">
            <md-checkbox v-model="postalSameAddress">
              Same as Business Address
            </md-checkbox>
          </div>
        </div>
        <div v-if="postalSameAddress === false" class="md-layout">
          <div class="md-layout-item md-small-size-100 md-size-100">
            <form-group label="" :validator-object="$v.business.postal_address">
              <template v-slot:input>
                <vue-google-autocomplete
                  id="postal-address"
                  ref="postalAddressInput"
                  classname="form-control md-input"
                  placeholder="Start typing..."
                  country="au"
                  @placechanged="getPostalData"
                ></vue-google-autocomplete>
              </template>
            </form-group>
          </div>
        </div>
        <div class="md-layout mt-3">
          <div class="md-layout-item md-size-100">
            <h6>Vehicles</h6>
          </div>
          <div class="d-flex flex-column">
            <div v-for="(input, i) in business.vehicles" :key="i">
              <div class="md-layout-item md-small-size-100 md-size-100">
                <md-field>
                  <label>Number Plate</label>
                  <md-input
                    v-model="input.number_plate"
                    class="text-uppercase"
                    type="text"
                  ></md-input>
                  <a href="#" @click.prevent="removeVehicle(i)"
                    ><md-icon style="color: rgb(115,38,38)">delete</md-icon></a
                  >
                </md-field>
              </div>
            </div>
          </div>
          <div class="w-100 mt-3">
            <md-button class="md-primary" @click="addVehicle()">
              Add Another Vehicle
            </md-button>
          </div>
        </div>
        <div
          v-if="
            !createMode &&
              this.$helpers.user.hasPermission(authUser, 'manage employees')
          "
        >
          <div class="md-layout mt-3">
            <div class="md-layout-item md-size-100">
              <h6>Employees</h6>
              <employee-pass-table
                ref="usersTable"
                :selectable="false"
                editable
              />
            </div>
          </div>
          <div v-if="showAddEmployee" class="md-layout mt-3">
            <div class="md-layout-item md-size-100">
              <h6>Add Employee</h6>
              <registration-form-abbr @userAdded="updateUsersList()" />
            </div>
          </div>
        </div>
        <div class="md-layout">
          <div
            class="md-layout-item md-size-100 d-flex align-items-center justify-content-end"
          >
            <md-button
              v-if="createMode"
              :disabled="
                !this.$helpers.user.hasPermission(
                  authUser,
                  'create business'
                ) || loading
              "
              class="md-raised md-success"
              @click="submitBusiness(true)"
              >Register Business</md-button
            >
            <md-button
              v-else
              :disabled="
                !this.$helpers.user.hasPermission(
                  authUser,
                  'update own business'
                ) || loading
              "
              class="md-raised md-success"
              @click="submitBusiness(false)"
              >Update Details</md-button
            >
            <md-progress-spinner
              class="mx-3"
              :md-diameter="30"
              :md-stroke="loading ? 4 : 0"
              md-mode="indeterminate"
            ></md-progress-spinner>
          </div>
        </div>
      </md-card-content>
    </md-card>
  </form>
</template>
<script>
import ApiService from "@/services/api";
import { mapGetters } from "vuex";
import businessModel from "@/models/business";
import {
  email,
  maxLength,
  minLength,
  numeric,
  required
} from "vuelidate/lib/validators";
import FormGroup from "@/components/Forms/FormGroup";
import auStates from "@/util/auStates";
import VueGoogleAutocomplete from "vue-google-autocomplete";
import EmployeePassTable from "@/views/Tables/employeePassTable";
import RegistrationFormAbbr from "@/views/Forms/RegistrationFormAbbr";
import { toastLaravelErrors } from "@/mixins";

export default {
  name: "EditBusinessForm",
  components: {
    EmployeePassTable,
    FormGroup,
    VueGoogleAutocomplete,
    RegistrationFormAbbr
  },
  mixins: [toastLaravelErrors],
  props: {
    dataBackgroundColor: {
      type: String,
      default: ""
    },
    createMode: {
      type: Boolean,
      default: false
    },
    showAddEmployee: {
      type: Boolean,
      default: true
    },
    lookupData: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      business: Object.assign({}, businessModel),
      businessTypes: [],
      postalSameAddress: false,
      loading: false,
      auStates
    };
  },
  computed: {
    ...mapGetters(["authUser"])
  },
  validations: {
    business: {
      business_name: {
        required,
        maxLength: maxLength(255)
      },
      trading_name: {
        required,
        maxLength: maxLength(255)
      },
      abn: {
        required,
        minLength: minLength(9),
        maxLength: maxLength(11)
      },
      email: {
        required,
        email
      },
      phone_number: {
        required
      },
      business_type: {
        required
      },
      contact_name: {
        required,
        maxLength: maxLength(255)
      },
      business_address: {
        required,
        maxLength: maxLength(255)
      },
      business_address_suburb: {
        required,
        maxLength: maxLength(255)
      },
      business_address_state: {
        required
      },
      business_address_postcode: {
        required,
        numeric,
        maxLength: maxLength(255)
      },
      postal_address: {
        required,
        maxLength: maxLength(255)
      },
      postal_address_suburb: {
        required,
        maxLength: maxLength(255)
      },
      postal_address_state: {
        required
      },
      postal_address_postcode: {
        required,
        numeric,
        maxLength: maxLength(255)
      },
      vehicles: {}
    }
  },
  created() {
    ApiService.auth.setCSRF();

    ApiService.business.getTypes().then(response => {
      this.businessTypes = response.data.data.business_types;
    });

    if (this.authUser.business) {
      this.business = Object.assign({}, this.authUser.business);
    }

    this.postalSameAddress = this.checkPostalSameAddress();
  },
  mounted() {
    if (this.lookupData) {
      this.business.abn = this.lookupData.abn;
      this.business.business_name = this.lookupData.lookupName;
      this.business.trading_name = this.lookupData.lookupName;
      this.business.contact_name = `${this.authUser.first_name} ${this.authUser.last_name}`;
    }

    if (!this.business.business_address) return;

    this.$refs.addressInput.update(this.buildAddress(false));
    this.$refs.postalAddressInput?.update(this.buildAddress(true));
  },
  methods: {
    addVehicle() {
      this.business.vehicles.push({ name: "" });
    },
    removeVehicle(index) {
      this.business.vehicles.splice(index, 1);
    },
    checkPostalSameAddress() {
      return (
        this.business.business_address === this.business.postal_address &&
        this.business.business_address_suburb ===
          this.business.postal_address_suburb &&
        this.business.business_address_state ===
          this.business.postal_address_state &&
        this.business.business_address_postcode ===
          this.business.postal_address_postcode
      );
    },
    setPostalAddressToSame() {
      this.business.postal_address = this.business.business_address;
      this.business.postal_address_suburb = this.business.business_address_suburb;
      this.business.postal_address_state = this.business.business_address_state;
      this.business.postal_address_postcode = this.business.business_address_postcode;
    },
    submitBusiness(isNewBusiness) {
      if (this.postalSameAddress) this.setPostalAddressToSame();

      if (!this.validate()) return;

      this.loading = true;

      // Create or update business
      let promise = isNewBusiness
        ? ApiService.business.createBusiness(this.business)
        : ApiService.business.updateBusiness(
            this.business,
            this.authUser.business_id
          );

      promise
        .then(() => {
          this.$store.dispatch("getAuthUser").then(() => {
            if (isNewBusiness || this.$route.path === "/confirm-business") {
              const user = this.$store.getters.authUser;
              this.$set(user, "new_pass", {
                name: "",
                price: 0,
                type: "none"
              });
              this.$store.dispatch("setCart", [user]).then(() => {
                this.$router.push("/cart");
              });
            } else {
              this.$router.push("/dashboard");
            }
          });
        })
        .catch(error => {
          if (error.response.status === 422) {
            this.toastLaravelErrors(error);
          } else {
            this.$toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    validate() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    getAddressData(data) {
      this.business.business_address = data.street_number + " " + data.route;
      this.business.business_address_suburb = data.locality;
      this.business.business_address_state = data.administrative_area_level_1;
      this.business.business_address_postcode = data.postal_code;
    },
    getPostalData(data) {
      this.business.postal_address = data.street_number + " " + data.route;
      this.business.postal_address_suburb = data.locality;
      this.business.postal_address_state = data.administrative_area_level_1;
      this.business.postal_address_postcode = data.postal_code;
    },
    buildAddress(postal = false) {
      return postal
        ? `${this.business.postal_address}, ${this.business.postal_address_suburb} ${this.business.postal_address_state}, Australia`
        : `${this.business.business_address}, ${this.business.business_address_suburb} ${this.business.business_address_state}, Australia`;
    },
    updateUsersList() {
      this.$refs.usersTable.updateTable();
    }
  }
};
</script>
<style></style>
